// Clark University: public college page (#2743), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - Clark's Common Data Set 2025-2026 (CDS_-2025_26.pdf), the file the
//    Strategic Analytics, Assessment and Institutional Research office links
//    from its Facts page (its Common Data Set page links no file); the
//    SOURCES url is that Facts page. The PDF's numbers do not come through as
//    text, so they were read from the rendered pages. C1 applicants, admits
//    and enrollees for Fall 2025; C8 test policy for Fall 2027; C21/C22 early
//    plans (the early decision counts are blank).
//    Quirks: the C1 residency table fills only the "unknown" column for
//    applicants and admits, so no residency split is shown; its enrolled row
//    totals 384 (159 in-state, 225 out-of-state), while the C1 enrollee rows
//    sum to 417 (182 men, 231 women, 4 unknown). The page uses the C1 rows.
//    G0 checks "2026-2027 costs not available at this time" (final by
//    4/1/2026), so the CDS cost section is not used.
//  - Clark Undergraduate Admissions: Apply (admissions rounds table),
//    Application process (test policy FAQ) and Early Decision (binding terms).
//    The ED page says ED applicants hear by Thanksgiving; the rounds table
//    says late December for Early Decision I. Both are shown.
//  - Clark Undergraduate Admissions, Cost and Financial Aid: cost of
//    attendance 2026-2027 (billed charges plus other possible expenses).
//  - College Scorecard, school id 165334 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'Clark Common Data Set 2025-2026',
        url: 'https://www.clarku.edu/stair/facts/',
        read: '2026-10-01',
    },
    apply: {
        label: 'Clark Undergraduate Admissions, Apply',
        url: 'https://www.clarku.edu/undergraduate-admissions/apply/',
        read: '2026-10-01',
    },
    process: {
        label: 'Clark Undergraduate Admissions, Application process',
        url: 'https://www.clarku.edu/undergraduate-admissions/apply/process/',
        read: '2026-10-01',
    },
    earlyDecision: {
        label: 'Clark Undergraduate Admissions, Early Decision',
        url: 'https://www.clarku.edu/undergraduate-admissions/apply/early-decision/',
        read: '2026-10-01',
    },
    costs: {
        label: 'Clark Undergraduate Admissions, Cost and Financial Aid',
        url: 'https://www.clarku.edu/undergraduate-admissions/cost-and-financial-aid/',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?165334-Clark-University',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'clark-university',
    kbId: 'clark_university',
    name: 'Clark University',
    shortName: 'Clark',
    location: 'Worcester, MA',
    control: 'Private',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (Clark style guide); decoration only.
    branding: {
        primary: '#CC0000',
        secondary: '#939598',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/clark_university_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 10936,
        admitted: 4426,
        enrolled: 417,
        source: 'cds',
        where: 'section C1',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Clark\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by school or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early decision and early action',
        note: 'Clark offers Early Decision I and II and a nonbinding, unrestricted Early Action plan, but its Common Data Set leaves the early decision counts blank, so it gives no early acceptance rate.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'January 15' },
        rows: [
            { plan: 'Early Decision I', deadline: 'November 1', decision: 'Late December', binding: true },
            { plan: 'Early Action', deadline: 'November 1', decision: 'Mid-January', binding: false },
            { plan: 'Early Decision II', deadline: 'January 15', decision: 'Early March', binding: true },
            { plan: 'Regular Decision', deadline: 'January 15', decision: 'Late March', binding: false },
        ],
        note: 'Clark\'s Early Decision page says ED applicants get their admission and scholarship decision by Thanksgiving; the rounds table on its Apply page lists late December for Early Decision I. There is no application fee. Your main application is what must arrive by the deadline; other materials may follow.',
        source: 'apply',
        binding: 'Early Decision I and II are binding: by applying ED you name Clark your top choice, and if admitted you withdraw your other applications and enroll. You, a parent or guardian and your school counselor sign the ED Agreement. Financial aid forms are due with the application (November 1 for ED I, January 15 for ED II); if you still cannot afford Clark after working with its financial aid office, you may ask to be released.',
        bindingSource: 'earlyDecision',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'Clark has been test-optional since 2012: you choose whether to send SAT or ACT scores, and applications are reviewed as a whole. Test scores are not factored into merit aid decisions.',
        source: 'process',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as not required for fall 2027 applicants, but considered if submitted.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'apply',
    },

    cost: {
        year: '2026-2027',
        rows: [
            { item: 'Tuition', amount: 62070 },
            { item: 'Student activity and programming fee', amount: 460 },
            { item: 'Orientation fee', amount: 400 },
            { item: 'Health and wellness fee', amount: 680 },
            { item: 'Housing (standard double)', amount: 8580 },
            { item: 'Food (standard meal plan)', amount: 5780 },
            { item: 'Books (estimated)', amount: 900 },
            { item: 'Transportation allowance', amount: 250 },
            { item: 'Miscellaneous (estimated)', amount: 800 },
            { item: 'Health insurance', amount: 2833 },
        ],
        total: 82753,
        note: 'Billed charges come to $77,970; the rest are other possible expenses. Massachusetts law requires students to join the Student Health Insurance Plan unless they show comparable coverage.',
        source: 'costs',
        netPrice: {
            average: 29446,
            byIncome: [
                { income: '$0 to $30,000', amount: 29477 },
                { income: '$30,001 to $48,000', amount: 13773 },
                { income: '$48,001 to $75,000', amount: 26236 },
                { income: '$75,001 to $110,000', amount: 30087 },
                { income: 'Over $110,000', amount: 41971 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Clark\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
